import type { resultType } from './Tracker';
import Tracker from './Tracker';
import type { initTracker } from './Tracker';

export interface TrackPageViewInterface {
  path: string;
}

export interface SetCustomDimensionInterface {
  name: string;
  value: string;
}

export interface TrackEventInterface {
  category: string;
  action: string;
  label: string;
  value: string;
}

export default class Native extends Tracker {
  public customVariableJsonMap: Record<string, string>;

  constructor(args: initTracker) {
    super({ ...args, type: 'native' });
    this.customVariableJsonMap = {};
    if (typeof window === 'undefined' || !window?.ZMOB_nativeAPI) {
      this.logger && this.logger.debug('no mob native api detected');
    }
  }

  private getMobNativeApi() {
    return window?.ZMOB_nativeAPI || null;
  }

  private noMobNativeApiError = this.result(false, {
    error: 'no mob native api',
  });

  private noPathError = this.result(false, {
    error: 'no path',
  });

  private noCategoryAndActionError = this.result(false, {
    error: 'no category and action',
  });

  setCustomDimensions = ({ name, value }: SetCustomDimensionInterface): void => {
    let sanitizedName = name.replace('dimension', '');
    sanitizedName = sanitizedName.replace('cd', '');
    this.customVariableJsonMap[sanitizedName] = value;
  };

  trackPageView = ({ path = '' }: TrackPageViewInterface): resultType => {
    if (typeof path !== 'string') {
      return this.noPathError;
    }

    const trimmedPath = path.trim();
    const mobNativeApi = this.getMobNativeApi();
    if (!mobNativeApi) {
      return this.noMobNativeApiError;
    }

    if (!trimmedPath) {
      return this.noPathError;
    }

    this.logger &&
      this.logger.debug(
        {
          path: trimmedPath,
          customDimensions: this.customVariableJsonMap,
        },
        'native trackPageView',
      );
    if (!this.drymode) {
      mobNativeApi.trackUAPageview(trimmedPath, JSON.stringify(this.customVariableJsonMap));
    }

    return this.result(true, {
      path: trimmedPath,
      customDimensions: this.customVariableJsonMap,
    });
  };

  trackEvent = ({ category, action, label, value }: TrackEventInterface): resultType => {
    const mobNativeApi = this.getMobNativeApi();
    if (!mobNativeApi) {
      return this.noMobNativeApiError;
    }

    if (!category || !action) {
      return this.noCategoryAndActionError;
    }

    this.logger &&
      this.logger.debug(
        {
          category,
          action,
          label,
          value,
          customDimensions: this.customVariableJsonMap,
        },
        'native trackEvent',
      );
    if (!this.drymode) {
      mobNativeApi.trackUAWebviewEvent(category, action, label, value, JSON.stringify(this.customVariableJsonMap));
    }

    return this.result(true, {
      category,
      action,
      label,
      value,
      customDimensions: this.customVariableJsonMap,
    });
  };
}
